<template>
  <div class="main">
    <slot v-if="promotionSignRequired">
      <JointLoanLineLimitIncreaseBanner :amount="newCreditLimit" />
    </slot>
    <slot v-else>
      <Header
        v-if="showHeader"
        navbar-type="goBack"
        @clicked="goBack"
        :screen-title="screenTitle"
        :showHelpBtn="true"
        :help-query-param="helpQueryParam"
      />
      <div v-if="!amlSurvey">
        <SelectCardType
          v-if="shouldShowSelectType"
          :shouldShowVirtualCard="isVirtualCardEnabled"
          @selectCardType="setCardType($event)"
          @limit-increase-banner-click="clickNewLimitAmountBanner"
        />
        <CreditCardDetails v-if="cardType === 'PHYSICAL'" />
        <VirtualCreditCardDetails v-if="cardType === 'VIRTUAL'" />
      </div>
      <AntiMoneyLaunderingSurvey
        class="mt-3"
        v-if="amlSurvey"
        @on-continue-click="saveSurvey"
      />
    </slot>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, provide, ref, computed } from "vue";
import Header from "@/shared-components/consumer/Header.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { getDeviceLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { useSummaryRolloutSelectors } from "../../hooks/useSummaryRolloutSelectors";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import AntiMoneyLaunderingSurvey from "@/apps/credit-line/components/Withdrawal/AntiMoneyLaunderingSurvey.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import {
  AvailableFeatures,
  Country,
  FeatureControlService,
} from "@/apps/credit-line/services/feature-control.service";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { CustomerType } from "@/apps/credit-line/models/CreditLineDetails";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import { DEFAULT_SETTING } from "@/shared-models/consumer/ISetting";
import SelectCardType from "../../components/CreditCard/CardDetails/SelectCardType.vue";
import { CardType } from "@/apps/credit-line/types/cardType";
import CreditCardDetails from "@/apps/credit-line/components/CreditCard/CardDetails.vue";
import VirtualCreditCardDetails from "@/apps/credit-line/components/CreditCard/VirtualCardDetails.vue";
import { useRouter } from "vue-router";
import { useNewLimitIncreaseOffer } from "@/apps/credit-line/hooks/useNewLimitIncreaseOffer";
import JointLoanLineLimitIncreaseBanner from "@/apps/credit-line/components/Offers/JointLoanLineLimitIncreaseBanner.vue";
import { AMLSettings } from "@/apps/credit-line/helpers/aml-settings.helper.ts";

declare const Android: AndroidInterface;

const cardType = ref<CardType | null>(null);
const shouldShowSelectType = ref<boolean>(true);
const router = useRouter();
const cardSteps = ref<string>("SELECT_TYPE");

const screenTitle = computed(() => {
  if (cardType.value === "PHYSICAL") {
    return t("PAYJOY_CARD_VIEWS.PHYSICAL_CARD_TITLE");
  } else {
    return t("PAYJOY_CARD_VIEWS.VIRTUAL_CARD_TITLE");
  }
});

const helpQueryParam = computed(() => {
  if (cardType.value === "PHYSICAL") {
    return "physical-card";
  } else if (cardType.value === "VIRTUAL") {
    return "virtual-card";
  } else {
    return "";
  }
});

const setCardType = (type: CardType) => {
  cardType.value = type;
  shouldShowSelectType.value = false;

  setShowHeader(true);

  cardSteps.value = type === "PHYSICAL" ? "PHYSICAL_CARD" : "VIRTUAL_CARD";
};

const { t, locale } = useI18n();
const { fetchSummary } = useSummaryRolloutSelectors();
const { newCreditLimit, promotionSignRequired } = useNewLimitIncreaseOffer();

// Initialize virtual card enabled setting - default to true
const isVirtualCardEnabled = ref<boolean>(true);

const settingsLoaded = ref<boolean>(false);

const initializeVirtualCardSettings = async () => {
  const cardsPageSettings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
  const googleWalletForPhysicalCardsSetting =
    cardsPageSettings["addToGoogleWalletButtonForPhysicalCard"] ?? null;
  const googleWalletForVirtualCardsSetting =
    cardsPageSettings["addToGoogleWalletButtonForVirtualCard"] ?? null;
  const virtualCardSetting =
    cardsPageSettings["virtualCards"] ?? DEFAULT_SETTING;

  googleWalletEnabledForPhysicalCard.value =
    googleWalletForPhysicalCardsSetting?.enabled ?? false;
  googleWalletEnabledForVirtualCard.value =
    googleWalletForVirtualCardsSetting?.enabled ?? false;

  // Creation Settings
  allowVirtualCardCreation.value =
    cardsPageSettings["allowVirtualCardCreation"]?.enabled ?? false;
  allowPhysicalCardActivation.value =
    cardsPageSettings["allowPhysicalCardActivation"]?.enabled ?? false;
  allowCardShipments.value =
    cardsPageSettings["allowCardShipments"]?.enabled ?? false;
  amlSettings.value = cardsPageSettings?.amlSurveyConfiguration?.values
    ?.formValuesRules as unknown as AMLSettings | null;

  // Use settings value if available, otherwise keep default true
  if (virtualCardSetting && virtualCardSetting !== DEFAULT_SETTING) {
    isVirtualCardEnabled.value = virtualCardSetting.enabled;
    settingsLoaded.value = true;
  }
};

const showHeader = ref<boolean>(false);
const CreditLineService = new Service();
const amlSurvey = ref<boolean>(false);
const FeatureControl = new FeatureControlService();
const userId = ref<number | undefined>();
const creditLineId = ref<number | undefined>();
const country = ref<string>("");
const activeStatus = ref<Array<string>>([
  PayJoyCardStatus.ACTIVE,
  PayJoyCardStatus.BLOCKED,
]);
const googleWalletEnabledForPhysicalCard = ref<boolean>(true);
const googleWalletEnabledForVirtualCard = ref<boolean>(true);
const allowVirtualCardCreation = ref<boolean>(false);
const allowPhysicalCardActivation = ref<boolean>(false);
const allowCardShipments = ref<boolean>(false);
const amlSettings = ref<AMLSettings | null>(null);

provide(
  "googleWalletEnabledForPhysicalCard",
  googleWalletEnabledForPhysicalCard
);
provide("googleWalletEnabledForVirtualCard", googleWalletEnabledForVirtualCard);
provide("allowVirtualCardCreation", allowVirtualCardCreation);
provide("allowPhysicalCardActivation", allowPhysicalCardActivation);
provide("allowCardShipments", allowCardShipments);
provide("amlSettings", amlSettings);

getDeviceLanguage().then((language: string) => {
  if (language) {
    locale.value = language;
  }
});

const goBack = () => {
  if (cardSteps.value === "SELECT_TYPE") {
    router.back();
  } else {
    shouldShowSelectType.value = true;
    cardSteps.value = "SELECT_TYPE";
    setShowHeader(false);
    cardType.value = null;
  }
  if (typeof Android.creditOfferAccepted === "function") {
    Android.creditOfferAccepted();
  }
};

const requireAmlSurvey = async (summary) => {
  CreditLineService.RequireAmlSurvey({
    deviceTag: AuthenticationHelper.getDeviceTag(),
    creditLineId: summary.creditLineId as number,
    userId: summary.userId,
  })
    .then((res) => {
      amlSurvey.value =
        res.data?.needSurvey &&
        FeatureControl.isFeatureEnabled(
          AvailableFeatures.AML_SURVEY,
          summary.country as Country
        ) &&
        summary.withdrawals.length == 0;
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    })
    .finally(() => {
      if (isAndroid()) {
        Android.finishedLoading();
      }
    });
};

const saveSurvey = (data) => {
  data.userId = userId.value;
  data.deviceTag = AuthenticationHelper.getDeviceTag();
  data.creditLineId = creditLineId.value;
  CreditLineService.SaveAmlSurvey(data)
    .then((res) => {
      if (res.data?.success) {
        // Only hide AML survey if there's no blocking answer
        if (!data.hasBlockingAnswer) {
          amlSurvey.value = false;
        }
      }
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
};

const clickNewLimitAmountBanner = (): void => {
  void router.push({ name: "LimitIncreaseAcceptanceView" });
};

onMounted(async () => {
  try {
    // Initialize virtual card settings first
    await initializeVirtualCardSettings();

    const res = await fetchSummary(true);

    if (res && res.customerType) {
      if (
        (res?.creditCardStatus === PayJoyCardStatus.ELIGIBLE ||
          PayJoyCardStatus.NONE) &&
        res?.customerType === CustomerType.SIMULTANEOUS_LOAN_LINE
      ) {
        showHeader.value = false;
      }
    }
    userId.value = res.userId;
    creditLineId.value = res.creditLineId as number;
    country.value = res.country;

    // Only for activated or ready to activate card
    if (activeStatus.value.includes(res.creditCardStatus)) {
      await requireAmlSurvey(res);
    }

    if (isAndroid()) {
      Android.finishedLoading();
    }
  } catch (error) {
    // Log error for metrics
    ErrorHandler.handleNetworkErrorAndLog(
      error,
      () => null,
      "getSummary::CreditCardDetailsView"
    );
    // Navigate to error display
    router.push({
      name: "CreditLineApiErrorDisplay",
      query: {
        returnHomeOnTryAgain: "true",
      },
    });
  }
});

const setShowHeader = (val: boolean) => (showHeader.value = val);
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
</style>
